/**
 * This component is intended to reflect the high level structure of your application,
 * and render any components that are common across all views, such as the header or
 * top-level navigation. All other components should be rendered by route handlers.
 */

import React, { useEffect, useRef } from 'react';
import * as FullStory from '@fullstory/browser';
import moment from 'moment';
import { useUser } from '@lore/auth';
import { useConfig } from '@lore/config';
import { Outlet, useNavigate } from 'react-router-dom';
import useRouter from '../hooks/useRouter';
import ThinNavigation from './_navigation/ThinNavigation';
import MobileNavigationHeader from './_navigation/MobileNavigationHeader';
import ScrollAnchorContext from '../context/ScrollAnchorContext';
import ErrorBoundary from './ErrorBoundary';
import Impersonation from './Impersonation';

export default function Layout() {
  const { location } = useRouter();
  const currentUser = useUser();
  const config = useConfig();
  const scrollAnchorRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'F8') {
        e.preventDefault();
        const isOnFlagsPage = window.location.pathname === '/feature-flags';
        navigate(isOnFlagsPage ? -1 : '/feature-flags');
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    const { enabled, appId, companyId, companyName } = config.intercom;
    const createdAt = moment(currentUser.data.createdAt).unix();

    if (enabled) {
      window.Intercom("boot", {
        app_id: appId,
        name: `${currentUser.data.firstName} ${currentUser.data.lastName}`,
        email: currentUser.data.email,
        created_at: createdAt,
        company: {
          id: companyId,
          name: companyName
        }
      });
    }
  }, []);

  useEffect(() => {
    const { enabled } = config.intercom;

    if (enabled) {
      window.Intercom('update');
    }
  }, [location]);

  useEffect(() => {
    const { enabled } = config.fullstory;

    if (enabled) {
      FullStory.setUserVars({
        uid: currentUser.id,
        displayName: `${currentUser.data.firstName} ${currentUser.data.lastName}`,
        email: currentUser.data.email,
      });
    }
  }, []);

  return (
    <ScrollAnchorContext.Provider value={scrollAnchorRef}>
      <div className="h-screen flex flex-col lg:flex-row">
        <Impersonation />
        <div className="w-24x hidden lg:block z-20 border-r border-gray-100">
          <nav
            className="flex flex-col -mr-0.5 border-r-2 border-cs-gray-100 py-8 bg-white overflow-y-auto h-screen"
            style={{ scrollbarGutter: 'stable' }}
          >
            <ThinNavigation/>
          </nav>
        </div>
        <div className="h-12x block lg:hidden z-20 border-b border-gray-100 bg-gray-50">
          <MobileNavigationHeader />
        </div>
        {/*<div className="w-72 hidden lg:block z-10">*/}
        {/*  <nav className="flex flex-col -mr-0.5 border-r-2 border-cs-gray-100 py-8 bg-white overflow-y-auto h-screen">*/}
        {/*    <ThickNavigation/>*/}
        {/*  </nav>*/}
        {/*</div>*/}
        <div ref={scrollAnchorRef} className="flex-1 overflow-y-auto">
          {/*<ScrollToTop key={`scroll-to-top-${location.pathname}`} />*/}
          <ErrorBoundary config={config}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
    </ScrollAnchorContext.Provider>
  );
}
